import type { AckRecordInput, AudienceInput, KnowledgeBase, KnowledgeBaseView, OwnerRef } from '@ralph/contracts'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  askToLend,
  changeBaseAccess,
  createKnowledgeBase,
  decideLendingRequest,
  deleteKnowledgeBase,
  deleteKnowledgeBaseDocument,
  fetchAgentCoverage,
  fetchAgentKnowledgeUsage,
  fetchAgentsUsingBase,
  fetchBaseAccess,
  fetchBaseFolders,
  fetchDocumentChunks,
  fetchKnowledgeBase,
  fetchKnowledgeBaseDocuments,
  fetchKnowledgeBases,
  fetchLendingRequest,
  type KnowledgeBasePatch,
  lendToAgent,
  moveKnowledgeBase,
  reprocessBase,
  reprocessDocument,
  searchKnowledgeBase,
  stopLending,
  updateKnowledgeBase,
  uploadKnowledgeBaseDocument,
  withdrawLendingRequest
} from '@/api/knowledge-bases.ts'
import { useI18n } from '@/i18n/index.ts'

/**
 * Knowledge bases (PRD-26), as queries.
 *
 * One rule is not boilerplate: every write that can change who gets what — access, lending, a
 * move, a deletion — invalidates the whole knowledge tree AND the marketplace tree. Coverage in
 * the builder and on an agent's page is computed per reader on the server; a write that left an
 * "Ask its managers" button on screen after the managers had said yes is the one kind of stale
 * this feature must not have.
 *
 * One thing polls, and only while it has to: a document list refetches every two seconds while
 * any file is still being read (PRD-24 D-5), and stops the moment none is.
 */

export const knowledgeBaseKeys = {
  all: ['knowledge-bases'] as const,
  list: (view: KnowledgeBaseView) => ['knowledge-bases', 'list', view] as const,
  base: (baseId: string) => ['knowledge-bases', 'base', baseId] as const,
  documents: (baseId: string) => ['knowledge-bases', 'documents', baseId] as const,
  chunks: (baseId: string, documentId: string) => ['knowledge-bases', 'chunks', baseId, documentId] as const,
  access: (baseId: string) => ['knowledge-bases', 'access', baseId] as const,
  agents: (baseId: string) => ['knowledge-bases', 'agents', baseId] as const,
  folders: (baseId: string) => ['knowledge-bases', 'folders', baseId] as const,
  coverage: (agentId: string, audience: AudienceInput) => ['knowledge-bases', 'coverage', agentId, audience] as const,
  usage: (agentId: string, days: number) => ['knowledge-bases', 'usage', agentId, days] as const,
  lendingRequest: (id: string) => ['knowledge-bases', 'lending-request', id] as const
}

/** What an access-shaped write can change: every knowledge read, every agent page, the session-free rest. */
const useAccessInvalidation = () => {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: knowledgeBaseKeys.all })
    void client.invalidateQueries({ queryKey: ['marketplace'] })
    void client.invalidateQueries({ queryKey: ['maker'] })
    void client.invalidateQueries({ queryKey: ['sharing'] })
    void client.invalidateQueries({ queryKey: ['groups', 'detail'] })
    // The picker's "than now" is the live reach; after a save it is the new one.
    void client.invalidateQueries({ queryKey: ['groups', 'reach'] })
  }
}

export const useKnowledgeBases = (view: KnowledgeBaseView = 'maintain', enabled = true) =>
  useQuery({
    queryKey: knowledgeBaseKeys.list(view),
    queryFn: () => fetchKnowledgeBases(view),
    enabled,
    retry: false,
    staleTime: 15_000,
    placeholderData: keepPreviousData
  })

/**
 * Every base this person can use — the ones they maintain and the ones shared with them — in one
 * list, by name. For the surfaces that offer bases to pick or to jump to (the command palette, an
 * agent's knowledge), where "who maintains it" is not the question being asked.
 */
/**
 * The two lists a person can search, merged and ordered by name.
 *
 * The ordering lives in `select` rather than in `queryFn`, and that placement is the point.
 * It used to be a bare `name.localeCompare(name)` inside the fetch — the BROWSER's collation,
 * not the product's, so two people reading the same screen in the same language got different
 * orders, and "Ática" sorted after "Azul" for one of them. Moving it to `select` binds it to
 * the reader's collator and re-runs it on every render, so switching language re-orders the
 * list immediately WITHOUT refetching (PRD-27 LOC-007) — which keying the query on the locale
 * would have done instead.
 */
export const useUsableKnowledgeBases = (enabled = true) => {
  const { compare } = useI18n()
  return useQuery({
    queryKey: [...knowledgeBaseKeys.all, 'list', 'usable'] as const,
    queryFn: async (): Promise<{ items: KnowledgeBase[]; nextCursor: null }> => {
      const [maintained, shared] = await Promise.all([fetchKnowledgeBases('maintain'), fetchKnowledgeBases('use')])
      const byId = new Map([...maintained.items, ...shared.items].map((base) => [base.id, base]))
      return { items: [...byId.values()], nextCursor: null }
    },
    select: (data) => ({ ...data, items: [...data.items].sort((a, b) => compare(a.name, b.name)) }),
    enabled,
    retry: false,
    staleTime: 30_000
  })
}

export const useKnowledgeBase = (baseId: string | null) =>
  useQuery({
    queryKey: knowledgeBaseKeys.base(baseId ?? ''),
    queryFn: () => fetchKnowledgeBase(baseId ?? ''),
    enabled: Boolean(baseId),
    retry: false,
    refetchOnWindowFocus: true
  })

export const useCreateKnowledgeBase = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; description: string; owner?: OwnerRef }) => createKnowledgeBase(input),
    onSuccess: () => client.invalidateQueries({ queryKey: knowledgeBaseKeys.all })
  })
}

export const useUpdateKnowledgeBase = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ baseId, ...patch }: { baseId: string } & KnowledgeBasePatch) => updateKnowledgeBase(baseId, patch),
    onSuccess: () => client.invalidateQueries({ queryKey: knowledgeBaseKeys.all })
  })
}

/** Deleting a base degrades every agent attached to it, so agents are invalidated too. */
export const useDeleteKnowledgeBase = () => {
  const invalidate = useAccessInvalidation()
  return useMutation({
    mutationFn: ({ baseId, confirmName }: { baseId: string; confirmName?: string }) =>
      deleteKnowledgeBase(baseId, confirmName),
    onSuccess: invalidate
  })
}

export const useMoveKnowledgeBase = () => {
  const invalidate = useAccessInvalidation()
  return useMutation({
    mutationFn: ({ baseId, to }: { baseId: string; to: OwnerRef }) => moveKnowledgeBase(baseId, to),
    onSuccess: invalidate
  })
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

const IN_PROGRESS = new Set(['pending', 'extracting', 'chunking', 'embedding'])

export const useKnowledgeBaseDocuments = (baseId: string | null, enabled = true) =>
  useQuery({
    queryKey: knowledgeBaseKeys.documents(baseId ?? ''),
    queryFn: () => fetchKnowledgeBaseDocuments(baseId ?? ''),
    enabled: enabled && Boolean(baseId),
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: (query) =>
      query.state.data?.items.some((document) => IN_PROGRESS.has(document.status)) ? 2_000 : false
  })

export const useDocumentChunks = (baseId: string | null, documentId: string | null) =>
  useQuery({
    queryKey: knowledgeBaseKeys.chunks(baseId ?? '', documentId ?? ''),
    queryFn: () => fetchDocumentChunks(baseId ?? '', documentId ?? ''),
    enabled: Boolean(baseId) && Boolean(documentId),
    retry: false
  })

/**
 * What a document write changes: the base's list and census, every passage list (a re-read
 * replaces them wholesale), and — for a synced document — its folder, which now leaves it out.
 */
const useDocumentInvalidation = (baseId: string) => {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: knowledgeBaseKeys.documents(baseId) })
    void client.invalidateQueries({ queryKey: knowledgeBaseKeys.base(baseId) })
    void client.invalidateQueries({ queryKey: ['knowledge-bases', 'list'] })
    void client.invalidateQueries({ queryKey: ['knowledge-bases', 'chunks', baseId] })
    void client.invalidateQueries({ queryKey: ['connectors'] })
  }
}

export const useUploadKnowledgeDocument = (baseId: string) => {
  const invalidate = useDocumentInvalidation(baseId)
  return useMutation({ mutationFn: (file: File) => uploadKnowledgeBaseDocument(baseId, file), onSuccess: invalidate })
}

export const useDeleteKnowledgeDocument = (baseId: string) => {
  const invalidate = useDocumentInvalidation(baseId)
  return useMutation({
    mutationFn: (documentId: string) => deleteKnowledgeBaseDocument(baseId, documentId),
    onSuccess: invalidate
  })
}

export const useReprocessDocument = (baseId: string) => {
  const invalidate = useDocumentInvalidation(baseId)
  return useMutation({
    mutationFn: (documentId: string) => reprocessDocument(baseId, documentId),
    onSuccess: invalidate
  })
}

export const useReprocessBase = (baseId: string) => {
  const invalidate = useDocumentInvalidation(baseId)
  return useMutation({ mutationFn: (all: boolean) => reprocessBase(baseId, all), onSuccess: invalidate })
}

export const useSearchKnowledgeBase = (baseId: string) =>
  useMutation({
    mutationFn: (input: { query: string; limit?: number; strategy?: 'hybrid' | 'semantic' | 'lexical' }) =>
      searchKnowledgeBase(baseId, input)
  })

// ---------------------------------------------------------------------------
// Who can use it, and the agents that use it (maintainers)
// ---------------------------------------------------------------------------

export const useBaseAccess = (baseId: string | null, enabled = true) =>
  useQuery({
    queryKey: knowledgeBaseKeys.access(baseId ?? ''),
    queryFn: () => fetchBaseAccess(baseId ?? ''),
    enabled: enabled && Boolean(baseId),
    retry: false
  })

export const useChangeBaseAccess = (baseId: string) => {
  const invalidate = useAccessInvalidation()
  return useMutation({
    mutationFn: (input: { access: AudienceInput; ackText: string; ifVersion?: number } & AckRecordInput) =>
      changeBaseAccess(baseId, input),
    onSuccess: invalidate
  })
}

export const useAgentsUsingBase = (baseId: string | null, enabled = true) =>
  useQuery({
    queryKey: knowledgeBaseKeys.agents(baseId ?? ''),
    queryFn: () => fetchAgentsUsingBase(baseId ?? ''),
    enabled: enabled && Boolean(baseId),
    retry: false
  })

export const useLendToAgent = (baseId: string) => {
  const invalidate = useAccessInvalidation()
  return useMutation({
    mutationFn: (input: { agentId: string; ackText: string } & AckRecordInput) => lendToAgent(baseId, input),
    onSuccess: invalidate
  })
}

export const useStopLending = (baseId: string) => {
  const invalidate = useAccessInvalidation()
  return useMutation({ mutationFn: (agentId: string) => stopLending(baseId, agentId), onSuccess: invalidate })
}

/** Asking touches the asker's own view only (the request's state on the agent's page). */
export const useAskToLend = () => {
  const invalidate = useAccessInvalidation()
  return useMutation({
    mutationFn: ({ baseId, agentId, note }: { baseId: string; agentId: string; note?: string }) =>
      askToLend(baseId, agentId, note ?? ''),
    onSuccess: invalidate
  })
}

export const useLendingRequest = (requestId: string | null) =>
  useQuery({
    queryKey: knowledgeBaseKeys.lendingRequest(requestId ?? ''),
    queryFn: () => fetchLendingRequest(requestId ?? ''),
    enabled: Boolean(requestId),
    retry: false
  })

export const useDecideLendingRequest = () => {
  const invalidate = useAccessInvalidation()
  return useMutation({
    mutationFn: ({
      requestId,
      ...input
    }: {
      requestId: string
      decision: 'approved' | 'declined'
      note?: string
      ackText?: string
    } & AckRecordInput) => decideLendingRequest(requestId, input),
    onSuccess: invalidate
  })
}

export const useWithdrawLendingRequest = () => {
  const invalidate = useAccessInvalidation()
  return useMutation({ mutationFn: (requestId: string) => withdrawLendingRequest(requestId), onSuccess: invalidate })
}

export const useBaseFolders = (baseId: string | null, enabled = true) =>
  useQuery({
    queryKey: knowledgeBaseKeys.folders(baseId ?? ''),
    queryFn: () => fetchBaseFolders(baseId ?? ''),
    enabled: enabled && Boolean(baseId),
    retry: false
  })

// ---------------------------------------------------------------------------
// An agent's knowledge, for the people who edit it
// ---------------------------------------------------------------------------

/**
 * Coverage against the audience being CHOSEN (GRP-045) — the builder sends what the picker holds,
 * so a first publication to a group shows its gaps before it goes out rather than after.
 */
export const useAgentCoverage = (agentId: string | null, audience: AudienceInput | null) =>
  useQuery({
    queryKey: knowledgeBaseKeys.coverage(agentId ?? '', audience ?? { kind: 'private', groupIds: [] }),
    queryFn: () => fetchAgentCoverage(agentId ?? '', audience ?? { kind: 'private', groupIds: [] }),
    enabled: Boolean(agentId) && audience !== null,
    retry: false,
    staleTime: 15_000,
    placeholderData: keepPreviousData
  })

/** Which of an agent's bases actually answer questions (AGM-120); gated on its panel being open. */
export const useAgentKnowledgeUsage = (agentId: string | null, days: number, enabled = true) =>
  useQuery({
    queryKey: knowledgeBaseKeys.usage(agentId ?? '', days),
    queryFn: () => fetchAgentKnowledgeUsage(agentId ?? '', days),
    enabled: enabled && Boolean(agentId),
    retry: false,
    staleTime: 60_000
  })
